// alarmNotifications.js
// Pilha de avisos de alarme crítico, no canto inferior direito.
//
// O canto superior direito já é ocupado pelo botão flutuante de filtro
// (.floating-button), por isso a pilha vive no inferior direito, como define o
// escopo aprovado. O controle de som saiu daqui para o cabeçalho
// (alarmAudioMenu.js): permanente não precisava significar sempre na frente.
//
// Recolher NÃO é encerrar. O aviso segue existindo, o som segue tocando e o
// ativo segue contado — o operador apenas desocupou a tela. Só o
// reconhecimento ou a normalização do ativo encerram um aviso.

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import AlarmRecognizeConfirm from "./alarmRecognizeConfirm";
import { useAlarmNotifications } from "../scripts/alarmNotificationContext";
import "../styles/alarm-notifications.css";

const AlarmNotifications = () => {
  const { t } = useTranslation();
  const notifications = useAlarmNotifications();
  const [avisoEmConfirmacao, setAvisoEmConfirmacao] = useState(null);

  // Fora do provider (telas isoladas em teste, por exemplo) o componente
  // simplesmente não renderiza, em vez de derrubar a árvore.
  const [showModal, setShowModal] = useState(false);
  if (!notifications) return null;

  const {
    notices,
    queuedCount,
    minimizedCount,
    acknowledgeNotice,
    minimizeNotice,
    expandNotices,
  } = notifications;

  const abrirReconhecimento = (notice) => {
    // A confirmação precisa do UUID do ativo para consultar os alarmes; sem o
    // catálogo carregado não há como abri-la, e o aviso permanece com a opção
    // de recolher.
    if (!notice.device) return;
    setAvisoEmConfirmacao(notice);
    setShowModal(true);
  };

  // Cancelar a confirmação NÃO é reconhecer. O operador pode abrir, decidir
  // tratar depois e cancelar: o aviso e o som têm de continuar. Encerrar aqui
  // permitiria calar um alarme crítico ativo sem reconhecê-lo — inclusive por
  // quem não tem permissão, já que a confirmação esconde o botão mas permite
  // o fechamento.
  const fecharModal = () => {
    setShowModal(false);
    setAvisoEmConfirmacao(null);
  };

  // Chamado apenas quando o reconhecimento foi de fato efetivado no back-end.
  // Encerra aviso e som na sessão de quem reconheceu, que é o alcance definido
  // no escopo: o reconhecimento não se propaga entre sessões.
  //
  // O nível de alarme DO ATIVO é derivado das suas variáveis por uma regra que
  // vive fora do front-end — pendência registrada no escopo. Sem essa regra não
  // há como afirmar que um alarme específico É a condição crítica exibida.
  //
  // Diante disso, o encerramento é conservador: só quando o alarme reconhecido
  // estava aberto E nenhum outro alarme do ativo segue aberto sem
  // reconhecimento. Havendo pendência, o aviso continua — errar mantendo o
  // alerta é preferível a calar um crítico que ninguém tratou.
  const aoReconhecer = (alarme, { abertosPendentes } = {}) => {
    if (!avisoEmConfirmacao) return;
    if (!alarme || alarme.end_time) return;
    if (abertosPendentes > 0) return;
    acknowledgeNotice(avisoEmConfirmacao.assetId);
  };

  return (
    <>
      <div className="alarm-toast-stack" data-testid="pilha-avisos">
        {notices.map((notice) => (
          <div
            className="alarm-toast"
            key={notice.assetId}
            data-testid="aviso-critico"
            role="alert"
          >
            <i
              className="bi bi-exclamation-octagon-fill alarm-toast-icon"
              aria-hidden="true"
            />

            <div className="alarm-toast-main">
              <span className="alarm-toast-asset" title={notice.name}>
                {notice.name}
              </span>
              <span className="alarm-toast-meta">
                {/* `restored` distingue um crítico herdado da sessão anterior de
                    uma transição recém-observada — informação que muda o que o
                    operador faz a seguir. */}
                {notice.restored
                  ? t("Crítico não reconhecido")
                  : t("Entrou em estado crítico")}
              </span>
            </div>

            <button
              type="button"
              className="alarm-toast-ack"
              disabled={!notice.device}
              onClick={() => abrirReconhecimento(notice)}
            >
              {t("Reconhecer")}
            </button>

            <button
              type="button"
              className="alarm-toast-minimize"
              title={t("Recolher aviso (o alarme continua ativo)")}
              aria-label={t("Recolher aviso (o alarme continua ativo)")}
              onClick={() => minimizeNotice(notice.assetId)}
            >
              <i className="bi bi-dash-lg" aria-hidden="true" />
            </button>
          </div>
        ))}

        {queuedCount > 0 && (
          <div className="alarm-toast-queue" data-testid="fila-avisos">
            {queuedCount} {t("aviso(s) aguardando na fila")}
          </div>
        )}

        {minimizedCount > 0 && (
          <button
            type="button"
            className="alarm-toast-collapsed"
            data-testid="avisos-minimizados"
            onClick={expandNotices}
          >
            <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
            <span>
              {minimizedCount} {t("alarme(s) crítico(s) recolhido(s)")}
            </span>
            <i className="bi bi-chevron-up" aria-hidden="true" />
          </button>
        )}
      </div>

      {avisoEmConfirmacao && (
        <AlarmRecognizeConfirm
          show={showModal}
          notice={avisoEmConfirmacao}
          onHide={fecharModal}
          onRecognized={aoReconhecer}
        />
      )}
    </>
  );
};

export default AlarmNotifications;
